import { useEffect, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, KeyValueList, PillTone, ProgressBar, SegmentedControl, StatCard, StatGrid, StatusPill } from '../../../ui'
import { formatDateTime } from '../commsInboxView/shared'
import { ReceivedView } from './ConversationList'
import { ALL_MAILBOXES, Bucket, LetterCategoryFunnel, LetterFunnel, MailboxFunnel, SenderCounts } from './types'
import { ExportGroupBy } from '../commsShared/exportDownload'
import { CategoryActions, CategoryStats, ReportMenu, SeeActionsLink, SenderLine, StatLine, isMixed } from './CategoryReport'
import { agentActionsHref } from '../commsShared/agentActions'
import styles from './styles.module.scss'

/** Sweep / health figures from comms/mailboxes/list for this address. */
export interface SweepCard {
    address: string
    enabled: boolean
    provider: string
    live: boolean
    phase: 'backfilling' | 'live' | 'paused' | 'failed' | 'disabled' | 'not_started'
    sinceDate: string | null
    lastFolderSyncAt: string | null
    lastSyncedAt: string | null
    lastError: string | null
    folderCount: number
    includedFolders: number
    estimatedTotal: number
    held: number
    missing: number
    progressPct: number | null
    uniqueEmails: number
    extraCopies: number
    /** Raw folder-item counts (an email filed in two folders counts twice); footnotes only. */
    serverItems: number
    storedItems: number
    ingested: number
    duplicates: number
    noise: number
    errors: number
    folders: { backfilling: number; delta: number; pending: number; paused: number; failed: number }
    stats: { preprocessed: number; preprocessOutstanding: number; extractedPct: number | null } | null
}

export interface SesHealth {
    state: 'live' | 'stale' | 'error' | 'unwired'
    lastReceivedAt: string | null
    lastRecordedAt: string | null
    staleAfterHours: number
    subscriptions: { comms: number | null; legacy: number | null; stale: number | null; pendingConfirmation: number | null; error: string | null }
    webhookPath: string
}

const PHASE: Record<SweepCard['phase'], { text: string; tone: PillTone }> = {
    backfilling: { text: 'Backfilling', tone: 'warn' },
    live: { text: 'Live (delta)', tone: 'good' },
    paused: { text: 'Paused', tone: 'neutral' },
    failed: { text: 'Failed', tone: 'bad' },
    disabled: { text: 'Disabled', tone: 'neutral' },
    not_started: { text: 'Not started', tone: 'info' },
}
const SES_STATE: Record<SesHealth['state'], { text: string; tone: PillTone; meaning: string }> = {
    live: { text: 'Live (webhook)', tone: 'good', meaning: 'Mail is arriving through the SES webhook and being recorded as it lands.' },
    stale: { text: 'Quiet', tone: 'warn', meaning: 'Nothing has arrived for a while. Lenders reply most working days, so check the SNS subscription and the SES receipt rule.' },
    unwired: { text: 'Not wired up', tone: 'bad', meaning: 'No live subscription points the inbound topic at this backend, so new mail is not being recorded.' },
    error: { text: 'Cannot check', tone: 'neutral', meaning: 'The SNS subscription check failed; mail may still be arriving.' },
}

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-GB'))
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : null)

/**
 * Every headline figure is unique emails. `held` is distinct emails we have
 * downloaded, `missing` is what is still on the server and not yet fetched,
 * and `estimatedTotal` is their sum; folders and filed copies never appear.
 */
/** Emails downloaded from this mailbox. An email sent to two of our mailboxes is stored once and listed under the mailbox that fetched it first. */
const downloadedCount = (f: MailboxFunnel, sweep: SweepCard | null): number => (sweep && !sweep.live ? Math.max(sweep.held, f.held) : f.held)

const downloadedSub = (f: MailboxFunnel, sweep: SweepCard | null, conv: MailboxFunnel['conversations']): string => {
    if (!sweep || sweep.live || sweep.progressPct == null) return conv ? `${fmt(conv.conversations)} email threads` : 'emails recorded'
    const parts = [sweep.missing > 0 ? `${fmt(sweep.missing)} still to download` : 'nothing left to download']
    const elsewhere = downloadedCount(f, sweep) - f.held
    if (elsewhere > 0) parts.push(`${fmt(elsewhere)} also arrived in another of our mailboxes and are listed there`)
    return parts.join(' · ')
}

/**
 * What to export: the whole mailbox by category, one category, or one letter
 * type (optionally one category within it); thread_outcome exports one row per
 * thread keyed by where it stands.
 */
export interface ExportSlice {
    kind: 'category' | 'letter_type_category' | 'thread_outcome'
    category?: string | null
    sendKind?: string | null
    /** Analytics report grouped by lender, claim reference or client; null/absent = every row. */
    groupBy?: ExportGroupBy | null
}

export const sliceKey = (s: ExportSlice) => `${s.kind}:${s.sendKind ?? ''}:${s.category ?? ''}`

/** One outcome group from comms/inbox/bucket-categories?view=threads: threads standing at this outcome. */
export interface OutcomeFunnel {
    key: string | null
    label: string
    description: string | null
    nextAction: string | null
    /** Threads. */
    total: number
    /** Threads with a client or claim. */
    linked: number
    last7d: number
    differs: number
    /** Emails inside these threads, and how many of them are automated mail. */
    emails: number | null
    automated: number | null
    from?: SenderCounts
}

type AnyCategory = LetterCategoryFunnel | MailboxFunnel['byCategory'][number] | OutcomeFunnel
type Unit = 'email' | 'thread'

/**
 * Threads by outcome for one slice of a mailbox (all of it, or one letter
 * type). Loaded on demand so the Overview only pays for it when shown.
 */
/** The Overview's "all mailboxes" scope is a pseudo-address; the Agent Actions page wants null for it. */
const scopeMailbox = (address: string | null) => (address && address !== ALL_MAILBOXES ? address : null)

const useOutcomes = (address: string | null, bucket: Bucket | 'all', enabled: boolean) => {
    const [state, setState] = useState<{ key: string; total: number; linked: number; items: OutcomeFunnel[] } | null>(null)
    const key = `${address ?? ''}|${bucket}`
    useEffect(() => {
        if (!enabled) return
        let cancelled = false
        api({ url: 'comms/inbox/bucket-categories', data: { sourceMailbox: address, bucket, view: 'threads' } })
            .then((resp) => {
                if (cancelled || !resp.data?.success) return
                setState({ key, total: resp.data.total || 0, linked: resp.data.linked || 0, items: (resp.data.categories || []).map((c: OutcomeFunnel) => ({ ...c, differs: 0 })) })
            })
            .catch(() => {
                if (!cancelled) setState(null)
            })
        return () => {
            cancelled = true
        }
    }, [address, bucket, enabled, key])
    return state && state.key === key ? state : null
}

const VIEW_OPTIONS: { value: ReceivedView; label: string }[] = [
    { value: 'threads', label: 'Threads' },
    { value: 'emails', label: 'Emails' },
]

interface TileProps {
    category: AnyCategory
    /** Denominator for the share bar. */
    whole: number
    onClick: () => void
    /** Download this category's emails with their clients and claims; grouped by lender / claim / client when asked. */
    onExport?: ((groupBy: ExportGroupBy | null) => void) | null
    exporting?: boolean
    /** What the figure counts. */
    unit?: Unit
    /** Agent Actions page for this category at this level. */
    actionsHref?: string | null
}

/** Emails (or threads) in a group that have a client or claim. Inside a letter container every email answers one of our letters, so all of them do. */
const linkedCount = (c: AnyCategory): number => ('linked' in c ? c.linked : 'linkedClaim' in c ? c.linkedLetter + c.linkedClaim : c.total)

/** The card's figures, one per line: with a client or claim, automated emails inside (threads), this week. */
const statLines = (c: AnyCategory, unit: Unit = 'email'): StatLine[] => {
    const lines: StatLine[] = [{ value: linkedCount(c), label: 'with a client or claim' }]
    if (unit === 'thread' && 'emails' in c && c.emails !== null && c.emails !== c.total) lines.push({ value: c.emails, label: 'emails inside' })
    if ('automated' in c && c.automated && c.key !== '__automated') lines.push({ value: c.automated, label: `automated email${c.automated === 1 ? '' : 's'} inside` })
    lines.push({ value: c.last7d, label: 'this week' })
    return lines
}

/** One category: count, share of the whole, what differs, the next step, and a download of the report. */
const CategoryTile = ({ category: c, whole, onClick, onExport = null, exporting = false, unit = 'email', actionsHref = null }: TileProps) => (
    <div className={styles.categoryCard}>
        <button type="button" className={styles.categoryMain} onClick={onClick} title={c.description ?? undefined}>
            <div className={styles.categoryTop}>
                <span className={styles.categoryLabel}>{c.label}</span>
                <span className={styles.categoryTotal}>{fmt(c.total)}</span>
            </div>
            <CategoryStats lines={statLines(c, unit)} from={'from' in c ? c.from ?? null : null} />
            {c.description && <p className={styles.categoryDesc}>{c.description}</p>}
            <div className={styles.categoryBar} aria-hidden>
                <span className={styles.barLetter} style={{ flexGrow: c.total }} />
                <span className={styles.barNone} style={{ flexGrow: Math.max(whole - c.total, 0) }} />
            </div>
            <div className={styles.categoryMeta}>
                <span>{pct(c.total, whole) ?? 0}% of these</span>
                {c.differs > 0 && <span className={styles.error}>{fmt(c.differs)} differ from our record</span>}
            </div>
        </button>
        {(c.nextAction || onExport || actionsHref) && (
            <div className={styles.categoryFoot}>
                {c.nextAction ? (
                    <div className={styles.categoryNext}>
                        <strong>Next step:</strong> {c.nextAction}
                    </div>
                ) : (
                    <span />
                )}
                <CategoryActions>
                    {onExport && c.key && <ReportMenu unit={unit} exporting={exporting} onExport={onExport} />}
                    {actionsHref && <SeeActionsLink href={actionsHref} />}
                </CategoryActions>
            </div>
        )}
    </div>
)

/** Categories shown as cards before the rest collapse into a table. */
const MAX_CARDS = 6
/** With this many or fewer, everything is a card: hiding one or two saves nothing. */
const ALL_CARDS_UP_TO = 8

interface GroupProps {
    categories: AnyCategory[]
    /** Denominator for shares. */
    whole: number
    onOpen: (c: AnyCategory) => void
    exportSliceFor?: ((c: AnyCategory) => ExportSlice) | null
    onExport?: ((slice: ExportSlice) => void) | null
    exporting?: string | null
    unit?: Unit
    /** Agent Actions link per category. */
    actionsHrefFor?: ((c: AnyCategory) => string) | null
}

/**
 * The big categories as cards, the long tail in a compact table behind
 * "Show N more", so a mailbox with thirty categories does not become a page
 * of tiles. Order is by count; the table keeps the same open/export actions.
 */
const CategoryGroup = ({ categories, whole, onOpen, exportSliceFor = null, onExport = null, exporting = null, unit = 'email', actionsHrefFor = null }: GroupProps) => {
    const [showAll, setShowAll] = useState(false)
    const sorted = [...categories].sort((a, b) => b.total - a.total)
    const collapsible = sorted.length > ALL_CARDS_UP_TO
    const cards = collapsible ? sorted.slice(0, MAX_CARDS) : sorted
    const rest = collapsible ? sorted.slice(MAX_CARDS) : []
    const restEmails = rest.reduce((n, c) => n + c.total, 0)
    const exportFor = (c: AnyCategory) => (onExport && exportSliceFor && c.key ? exportSliceFor(c) : null)
    return (
        <>
            <div className={styles.categoryGrid}>
                {cards.map((c) => {
                    const slice = exportFor(c)
                    return <CategoryTile key={c.key ?? '__none'} category={c} whole={whole} unit={unit} onClick={() => onOpen(c)} onExport={slice ? (groupBy) => onExport!({ ...slice, groupBy }) : null} exporting={slice ? exporting === sliceKey(slice) : false} actionsHref={actionsHrefFor?.(c) ?? null} />
                })}
            </div>
            {rest.length > 0 && (
                <div className={styles.moreCategories}>
                    <Button size="sm" variant="link" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
                        {showAll ? 'Hide' : 'Show'} {fmt(rest.length)} smaller {unit === 'thread' ? `outcome${rest.length === 1 ? '' : 's'}` : `categor${rest.length === 1 ? 'y' : 'ies'}`} · {fmt(restEmails)} {unit}s
                    </Button>
                    {showAll && (
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{unit === 'thread' ? 'Outcome' : 'Category'}</th>
                                        <th className={styles.num}>{unit === 'thread' ? 'Threads' : 'Emails'}</th>
                                        <th className={styles.num}>With a client or claim</th>
                                        <th className={styles.num}>Share</th>
                                        <th className={styles.num}>This week</th>
                                        <th>Next step</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rest.map((c) => {
                                        const slice = exportFor(c)
                                        return (
                                            <tr key={c.key ?? '__none'} className={styles.clickableRow} onClick={() => onOpen(c)} title={c.description ?? undefined}>
                                                <td>
                                                    <span className={styles.categoryLabel}>{c.label}</span>
                                                    {c.differs > 0 && <span className={styles.error}> · {fmt(c.differs)} differ from our record</span>}
                                                </td>
                                                <td className={styles.num}>{fmt(c.total)}</td>
                                                <td className={styles.num}>{fmt(linkedCount(c))}</td>
                                                <td className={styles.num}>{pct(c.total, whole) ?? 0}%</td>
                                                <td className={styles.num}>{c.last7d > 0 ? fmt(c.last7d) : '—'}</td>
                                                <td className={styles.muted}>{c.nextAction ?? '—'}</td>
                                                <td className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
                                                    <Button size="sm" variant="link" onClick={() => onOpen(c)}>
                                                        Open
                                                    </Button>
                                                    {slice && <ReportMenu unit={unit} exporting={exporting === sliceKey(slice)} onExport={(groupBy) => onExport!({ ...slice, groupBy })} />}
                                                    {actionsHrefFor && <SeeActionsLink href={actionsHrefFor(c)} />}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

interface LetterProps {
    address: string | null
    letter: LetterFunnel
    conversations: number | null
    onOpenBucket: (bucket: Bucket | 'all', category?: string | null, view?: ReceivedView | null) => void
    onExport?: ((slice: ExportSlice) => void) | null
    exporting?: string | null
    view: ReceivedView
    onViewChange: (view: ReceivedView) => void
}


/**
 * The container for one type of letter we sent: every email in this mailbox
 * that answers one, broken down by what the reply said.
 */
const LetterContainer = ({ address, letter, conversations, onOpenBucket, onExport = null, exporting = null, view, onViewChange }: LetterProps) => {
    const categorised = letter.total - letter.waiting
    const all: ExportSlice = view === 'threads' ? { kind: 'thread_outcome', sendKind: letter.kind } : { kind: 'letter_type_category', sendKind: letter.kind }
    const outcomes = useOutcomes(address, letter.bucket, view === 'threads')
    return (
        <Card
            as="section"
            padding="sm"
            title={
                <>
                    {letter.kind === 'initial_loc' ? 'Letters of complaint' : `${letter.label}s`}
                    <StatusPill size="sm" tone="info" round>
                        {fmt(letter.total)}
                    </StatusPill>
                </>
            }
            subtitle={`${fmt(letter.total)} emails in this mailbox answer ${letter.kind === 'initial_loc' ? 'letters of complaint' : `${letter.label.toLowerCase()}s`} we sent${conversations != null ? ` · ${fmt(conversations)} threads` : ''}${letter.waiting > 0 ? ` · ${fmt(letter.waiting)} waiting to be read` : ''} · ${fmt(letter.last7d)} this week · last ${formatDateTime(letter.lastReceivedAt)}`}
            actions={
                <>
                    <SegmentedControl<ReceivedView> aria-label="Count by" value={view} onChange={onViewChange} options={VIEW_OPTIONS} />
                    {onExport && (
                        <Button size="sm" variant="ghost" onClick={() => onExport(all)} loading={exporting === sliceKey(all)} title={view === 'threads' ? 'Download every thread that answers this letter type, by where it stands, with its client and claim (.xlsx)' : 'Download every reply to this letter type here, by category, with its client and claim (.xlsx)'}>
                            Export .xlsx
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => onOpenBucket(letter.bucket, null, view)}>
                        Open all
                    </Button>
                </>
            }
        >
            {view === 'threads' ? (
                outcomes === null ? (
                    <p className={styles.note}>Loading threads…</p>
                ) : outcomes.items.length === 0 ? (
                    <p className={styles.note}>Nothing here has been read yet.</p>
                ) : (
                    <>
                        <p className={styles.note}>Where each thread stands: its newest email that is not automated mail. {fmt(outcomes.total)} threads · {fmt(outcomes.linked)} with a client or claim.</p>
                        <CategoryGroup
                            unit="thread"
                            categories={outcomes.items}
                            whole={outcomes.total}
                            onOpen={(c) => onOpenBucket(letter.bucket, c.key, 'threads')}
                            actionsHrefFor={(c) => agentActionsHref({ mailbox: scopeMailbox(address), view: 'threads', bucket: letter.bucket, category: c.key })}
                            exportSliceFor={(c) => ({ kind: 'thread_outcome', sendKind: letter.kind, category: c.key })}
                            onExport={onExport}
                            exporting={exporting}
                        />
                    </>
                )
            ) : letter.byCategory.length === 0 ? (
                <p className={styles.note}>Nothing here has been read yet.</p>
            ) : (
                <CategoryGroup
                    categories={letter.byCategory}
                    whole={categorised}
                    onOpen={(c) => onOpenBucket(letter.bucket, c.key, 'emails')}
                    actionsHrefFor={(c) => agentActionsHref({ mailbox: scopeMailbox(address), view: 'emails', bucket: letter.bucket, category: c.key })}
                    exportSliceFor={(c) => ({ kind: 'letter_type_category', sendKind: letter.kind, category: c.key })}
                    onExport={onExport}
                    exporting={exporting}
                />
            )}
        </Card>
    )
}

interface Props {
    funnel: MailboxFunnel | null
    sweep: SweepCard | null
    sesHealth: SesHealth | null
    onOpenBucket: (bucket: Bucket | 'all', category?: string | null, view?: ReceivedView | null) => void
    /** Download a report for a slice of this mailbox; null hides the export buttons. */
    onExport?: ((slice: ExportSlice) => void) | null
    /** sliceKey of the export in flight, if any. */
    exporting?: string | null
    /** Threads or emails: shared with the Received tab so the same figure means the same thing everywhere. */
    view: ReceivedView
    onViewChange: (view: ReceivedView) => void
}

/**
 * One mailbox, top down: how the mail gets here (sweep or live stream), how
 * much of it we hold and have read, what it is about (categories across the
 * whole mailbox), then one container per type of letter we sent whose replies
 * land here (letters of complaint first), each broken down by category. Mail
 * outside every container is just mailbox mail; it is not labelled.
 * Every card opens the Received tab on that slice.
 */
export const OverviewTab = ({ funnel, sweep, sesHealth, onOpenBucket, onExport = null, exporting = null, view, onViewChange: setView }: Props) => {
    const byCategoryAll: ExportSlice = { kind: 'category' }
    const byOutcomeAll: ExportSlice = { kind: 'thread_outcome' }
    const outcomes = useOutcomes(funnel?.address ?? null, 'all', Boolean(funnel) && view === 'threads')
    if (!funnel && !sweep) return <Banner tone="info">No emails have been recorded from this mailbox yet. Once a sweep has run (or, for the SES stream, once mail arrives) the funnel appears here.</Banner>

    const phase = sweep ? (sweep.live && sesHealth ? SES_STATE[sesHealth.state] : PHASE[sweep.phase]) : null
    const conv = funnel?.conversations ?? null
    const f = funnel

    return (
        <div className={styles.stack}>
            {sweep && (
                <Card
                    as="section"
                    padding="sm"
                    title={
                        <>
                            {sweep.live ? 'Live stream' : 'Sweep'}
                            {phase && (
                                <StatusPill tone={sweep.enabled ? phase.tone : 'neutral'} dot size="sm">
                                    {sweep.enabled ? phase.text : 'Disabled'}
                                </StatusPill>
                            )}
                        </>
                    }
                    subtitle={
                        sweep.live
                            ? `Mail arrives through Amazon SES and is recorded as it lands · since ${sweep.sinceDate ? new Date(sweep.sinceDate).toLocaleDateString('en-GB') : 'the beginning'}`
                            : `${sweep.provider} · since ${sweep.sinceDate ? new Date(sweep.sinceDate).toLocaleDateString('en-GB') : 'the beginning'} · folders discovered ${formatDateTime(sweep.lastFolderSyncAt)} · last sync ${formatDateTime(sweep.lastSyncedAt)}`
                    }
                >
                    {sweep.live ? (
                        <div className={styles.stack} style={{ gap: 8 }}>
                            <p className={styles.note}>{sesHealth ? SES_STATE[sesHealth.state].meaning : 'Mail arrives on its own: SES receives the reply, stores it and notifies this backend.'} Nothing to sweep.</p>
                            {sesHealth && (
                                <KeyValueList
                                    columns
                                    items={[
                                        { label: 'Last email recorded', value: `${formatDateTime(sesHealth.lastRecordedAt)}${sesHealth.lastReceivedAt ? ` (sent ${formatDateTime(sesHealth.lastReceivedAt)})` : ''}` },
                                        { label: 'Webhook', value: sesHealth.webhookPath, mono: true },
                                        { label: 'SNS subscriptions', value: sesHealth.subscriptions.error ? `could not check: ${sesHealth.subscriptions.error}` : `${fmt(sesHealth.subscriptions.comms)} on /comms/webhooks · ${fmt(sesHealth.subscriptions.legacy)} legacy${sesHealth.subscriptions.stale ? ` · ${fmt(sesHealth.subscriptions.stale)} stale` : ''}` },
                                        { label: 'Counts as quiet after', value: `${sesHealth.staleAfterHours}h without mail` },
                                    ]}
                                />
                            )}
                        </div>
                    ) : (
                        <>
                            <ProgressBar value={sweep.progressPct ?? 0} tone={sweep.phase === 'live' ? 'good' : sweep.phase === 'failed' ? 'bad' : 'accent'} showPercentage={false} thin />
                            <div className={styles.counts} style={{ marginTop: 8 }}>
                                <span>
                                    <strong className={styles.pct}>{sweep.progressPct == null ? '—' : `${sweep.progressPct}%`}</strong> <strong>{fmt(sweep.held)}</strong> unique emails downloaded
                                </span>
                                <span>
                                    <strong>{fmt(sweep.missing)}</strong> still to download
                                </span>
                                <span>
                                    <strong>{fmt(sweep.noise)}</strong> noise
                                </span>
                                {sweep.errors > 0 && (
                                    <span className={styles.error}>
                                        <strong>{fmt(sweep.errors)}</strong> errors
                                    </span>
                                )}
                                <span>
                                    folders: {sweep.includedFolders}/{sweep.folderCount} included · {sweep.folders.backfilling} backfilling · {sweep.folders.delta} live · {sweep.folders.pending} pending
                                    {sweep.folders.paused ? ` · ${sweep.folders.paused} paused` : ''}
                                    {sweep.folders.failed ? ` · ${sweep.folders.failed} failed` : ''}
                                </span>
                                {sweep.stats && (
                                    <span>
                                        <strong>{fmt(sweep.stats.preprocessOutstanding)}</strong> awaiting preprocessing
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                    {sweep.lastError && (
                        <Banner tone="bad" style={{ marginTop: 12 }}>
                            {sweep.lastError}
                        </Banner>
                    )}
                </Card>
            )}

            {f && (
                <>
                    <Card as="section" padding="sm" title="Mail in this mailbox" subtitle={`${fmt(downloadedCount(f, sweep))} emails downloaded · ${fmt(f.last7d)} arrived this week · last received ${formatDateTime(f.lastReceivedAt)}${conv ? ` · ${fmt(conv.conversations)} email threads` : ''}`}>
                        <StatGrid minColumn={170}>
                            {sweep && !sweep.live && sweep.missing > 0 && (
                                <StatCard compact label="Still to download" value={sweep.missing} tone="warn" sub="on the mail server, not yet fetched" />
                            )}
                            <StatCard compact label="Downloaded" value={downloadedCount(f, sweep)} sub={downloadedSub(f, sweep, conv)} onClick={() => onOpenBucket('all')} />
                            <StatCard compact label="Waiting to be read" value={f.waiting} tone={f.waitingFailed > 0 ? 'bad' : f.waiting > 0 ? 'warn' : 'good'} sub={f.waitingFailed > 0 ? `${fmt(f.waitingFailed)} could not be read` : 'queued for the pipeline'} onClick={() => onOpenBucket('waiting')} />
                            <StatCard compact label="Read and categorised" value={f.read} tone="good" sub={`${pct(f.read, f.held - f.archived) ?? '—'}% of live mail · ${fmt(f.byCategory.length)} categories`} onClick={() => onOpenBucket('read')} />
                            <StatCard compact label="Archived" value={f.archived} tone="muted" sub="noise and filed" onClick={() => onOpenBucket('archived')} />
                            {isMixed(f.from) && (
                                <StatCard compact label="Who wrote to us" value={f.from.client >= f.from.lender ? `${pct(f.from.client, f.held - f.archived) ?? 0}%` : `${pct(f.from.lender, f.held - f.archived) ?? 0}%`} unit={f.from.client >= f.from.lender ? ' clients' : ' lenders'} format={false} sub={<SenderLine from={f.from} />} />
                            )}
                        </StatGrid>
                        {conv && (
                            <div className={styles.counts} style={{ marginTop: 10 }}>
                                <span>Email threads:</span>
                                <StatusPill size="sm" tone={conv.awaitingUs > 0 ? 'warn' : 'neutral'}>
                                    {fmt(conv.awaitingUs)} awaiting us
                                </StatusPill>
                                <StatusPill size="sm" tone="info">
                                    {fmt(conv.awaitingLender)} awaiting lender
                                </StatusPill>
                                <StatusPill size="sm" tone="info">
                                    {fmt(conv.awaitingClient)} awaiting client
                                </StatusPill>
                                <StatusPill size="sm">{fmt(conv.archived)} closed</StatusPill>
                                {conv.agentOwned > 0 && (
                                    <StatusPill size="sm" tone="accent">
                                        {fmt(conv.agentOwned)} agent-owned
                                    </StatusPill>
                                )}
                            </div>
                        )}
                    </Card>

                    <Card
                        as="section"
                        padding="sm"
                        title={view === 'threads' ? 'Where threads stand' : 'What the mail is about'}
                        subtitle={
                            view === 'threads'
                                ? `Every email thread in this mailbox by its outcome: the newest email that is not automated mail${outcomes ? ` · ${fmt(outcomes.total)} threads · ${fmt(outcomes.linked)} with a client or claim` : ''}`
                                : `Every read email in this mailbox by its own category · ${fmt(f.read)} emails${f.differs > 0 ? ` · ${fmt(f.differs)} quote details that differ from our record` : ''}`
                        }
                        actions={
                            <>
                                <SegmentedControl<ReceivedView> aria-label="Count by" value={view} onChange={setView} options={VIEW_OPTIONS} />
                                {onExport && view === 'threads' && (
                                    <Button size="sm" variant="ghost" onClick={() => onExport(byOutcomeAll)} loading={exporting === sliceKey(byOutcomeAll)} title="Download every thread in this mailbox, by where it stands, with its client and claim (.xlsx)">
                                        Export all threads .xlsx
                                    </Button>
                                )}
                                {onExport && view === 'emails' && (
                                    <Button size="sm" variant="ghost" onClick={() => onExport(byCategoryAll)} loading={exporting === sliceKey(byCategoryAll)} title="Download every read email in this mailbox, by category, with its client and claim (.xlsx)">
                                        Export all categories .xlsx
                                    </Button>
                                )}
                            </>
                        }
                    >
                        {view === 'threads' ? (
                            outcomes === null ? (
                                <p className={styles.note}>Loading threads…</p>
                            ) : outcomes.items.length === 0 ? (
                                <p className={styles.note}>No threads yet.</p>
                            ) : (
                                <CategoryGroup unit="thread" categories={outcomes.items} whole={outcomes.total} onOpen={(c) => onOpenBucket('all', c.key, 'threads')} actionsHrefFor={(c) => agentActionsHref({ mailbox: scopeMailbox(f.address), view: 'threads', bucket: 'all', category: c.key })} exportSliceFor={(c) => ({ kind: 'thread_outcome', category: c.key })} onExport={onExport} exporting={exporting} />
                            )
                        ) : f.byCategory.length === 0 ? (
                            <p className={styles.note}>No emails have been categorised yet.</p>
                        ) : (
                            <CategoryGroup categories={f.byCategory} whole={f.read} onOpen={(c) => onOpenBucket('read', c.key, 'emails')} actionsHrefFor={(c) => agentActionsHref({ mailbox: scopeMailbox(f.address), view: 'emails', bucket: 'all', category: c.key })} exportSliceFor={(c) => ({ kind: 'category', category: c.key })} onExport={onExport} exporting={exporting} />
                        )}
                    </Card>

                    {f.byLetter.map((letter) => (
                        <LetterContainer key={letter.kind} address={f.address} letter={letter} conversations={conv?.byLetter?.[letter.kind] ?? null} onOpenBucket={onOpenBucket} onExport={onExport} exporting={exporting} view={view} onViewChange={setView} />
                    ))}
                </>
            )}
        </div>
    )
}
